import * as React from "react";
import { Briefcase, Building2, MapPin, ShieldCheck, UserRound } from "lucide-react";

import { api } from "../lib/api.js";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog.js";
import { Avatar } from "./ui/avatar.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { AsyncButton } from "./ui/async-button.js";
import { FileInput } from "./ui/file-input.js";
import { ProfilePhotoCropper, CROP_VIEW_PX } from "./ProfilePhotoCropper.js";
import { useMe, useMeLoading, useSetMyPhoto } from "../context/MeContext.js";
import {
  checkPhotoDimensions, checkPhotoFile, renderCrop, PHOTO_RULES_TEXT, type CropState,
} from "../lib/profilePhoto.js";

interface Picked {
  url: string;
  img: HTMLImageElement;
}

const CENTRED: CropState = { zoom: 1, x: 0, y: 0 };

function errMessage(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { message?: string } } })?.response;
  return r?.data?.message ?? (e instanceof Error && e.message !== "validation" ? e.message : fallback);
}

interface ProfileCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Centre of the avatar that opened the card, in viewport px: the card zooms out of it. */
  origin: { x: number; y: number } | null;
  /** Shown until the profile has loaded. */
  fallbackName: string;
}

/** HRMS-0653: the signed-in user's own card: photo, role details, and photo editing. */
export function ProfileCardDialog({ open, onOpenChange, origin, fallbackName }: ProfileCardDialogProps) {
  const me = useMe();
  const loading = useMeLoading();
  const setPhotoUrl = useSetMyPhoto();
  const [picked, setPicked] = React.useState<Picked | null>(null);
  const [crop, setCrop] = React.useState<CropState>(CENTRED);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  const name = me.fullName || fallbackName;

  const clearPicked = React.useCallback(() => {
    setPicked(p => {
      if (p) URL.revokeObjectURL(p.url);
      return null;
    });
    setCrop(CENTRED);
    setErr(null);
  }, []);

  // Live preview of exactly what will be saved, rendered at 512x512 and shown at
  // the sizes the photo is used at. Debounced so dragging stays smooth.
  // The old preview URL is revoked only once its replacement is ready, so the
  // previews never blank out mid-drag.
  const previewRef = React.useRef<string | null>(null);
  const swapPreview = React.useCallback((url: string | null) => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = url;
    setPreview(url);
  }, []);
  React.useEffect(() => {
    if (!picked) { swapPreview(null); return; }
    let cancelled = false;
    const t = window.setTimeout(() => {
      renderCrop(picked.img, crop, CROP_VIEW_PX)
        .then(blob => {
          if (cancelled) return;
          swapPreview(URL.createObjectURL(blob));
        })
        .catch(() => { if (!cancelled) swapPreview(null); });
    }, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [picked, crop, swapPreview]);
  React.useEffect(() => () => swapPreview(null), [swapPreview]);

  const handleOpenChange = (next: boolean) => {
    if (!next) clearPicked();
    onOpenChange(next);
  };

  const pickFile = async (file: File | null) => {
    if (!file) return;
    setErr(null);
    const typeErr = checkPhotoFile(file);
    if (typeErr) { setErr(typeErr); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch {
      URL.revokeObjectURL(url);
      setErr("That file couldn't be read as an image.");
      return;
    }
    const dimErr = checkPhotoDimensions(img.naturalWidth, img.naturalHeight);
    if (dimErr) { URL.revokeObjectURL(url); setErr(dimErr); return; }
    clearPicked();
    setPicked({ url, img });
  };

  const save = async () => {
    if (!picked) { setErr("Choose a photo first."); throw new Error("validation"); }
    try {
      setErr(null);
      const blob = await renderCrop(picked.img, crop, CROP_VIEW_PX);
      const { data: up } = await api.post<{ uploadUrl: string; key: string }>("/me/photo/upload-url", {
        mimeType: blob.type, size: blob.size,
      });
      const put = await fetch(up.uploadUrl, { method: "PUT", body: blob, headers: { "Content-Type": blob.type } });
      if (!put.ok) throw new Error("The photo upload failed. Please try again.");
      const { data } = await api.put<{ photoUrl: string | null }>("/me/photo", { key: up.key });
      setPhotoUrl(data.photoUrl);
      clearPicked();
    } catch (e) {
      setErr(errMessage(e, "Couldn't save the photo."));
      throw e;
    }
  };

  const remove = async () => {
    try {
      setErr(null);
      await api.delete("/me/photo");
      setPhotoUrl(null);
    } catch (e) {
      setErr(errMessage(e, "Couldn't remove the photo."));
      throw e;
    }
  };

  const office = [me.officeName, me.officeCity].filter(Boolean).join(" · ");
  const details: { icon: typeof Briefcase; label: string; value: React.ReactNode }[] = [
    { icon: Briefcase, label: "Designation", value: me.designation || "—" },
    { icon: Building2, label: "Department", value: me.departmentName || "—" },
    { icon: MapPin, label: "Office", value: office || "—" },
    { icon: UserRound, label: "Manager", value: me.managerName || "—" },
    {
      icon: ShieldCheck,
      label: "Probation",
      value: loading
        ? "—"
        : <Badge tone={me.isOnProbation ? "warning" : "success"}>{me.isOnProbation ? "On probation" : "Confirmed"}</Badge>,
    },
  ];

  // The card zooms out of the avatar's measured position; offset from the
  // viewport centre, where the dialog rests, so it can't be a static class.
  const originStyle = origin
    ? ({
        "--pc-from-x": `${origin.x - window.innerWidth / 2}px`,
        "--pc-from-y": `${origin.y - window.innerHeight / 2}px`,
      } as React.CSSProperties)
    : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="profile-card max-w-sm gap-0 overflow-x-hidden rounded-3xl p-0"
        overlayClassName="profile-card-overlay"
        style={originStyle}
      >
        <div className="profile-card-hero flex flex-col items-center gap-1 bg-linear-to-b from-brand-50 to-surface-0 px-6 pt-8 pb-5 text-center">
          <Avatar
            src={picked ? preview ?? me.photoUrl : me.photoUrl}
            name={name}
            size="xl"
            className="profile-card-photo mb-3 shadow-lg ring-4 ring-surface-0"
          />
          <DialogTitle className="text-lg font-bold text-surface-900">{name}</DialogTitle>
          <DialogDescription className="text-[13px] text-surface-500">
            {[me.designation, me.employeeCode].filter(Boolean).join(" · ") || me.email}
          </DialogDescription>
        </div>

        <div className="flex flex-col gap-4 px-6 pt-1 pb-6">
          {picked ? (
            <>
              <ProfilePhotoCropper
                src={picked.url}
                width={picked.img.naturalWidth}
                height={picked.img.naturalHeight}
                crop={crop}
                onCropChange={setCrop}
              />
              <div className="flex items-end justify-center gap-4" aria-label="Preview at the sizes your photo is shown">
                {(["sm", "md", "lg"] as const).map(size => (
                  <div key={size} className="flex flex-col items-center gap-1">
                    <Avatar src={preview} name={name} size={size} />
                    <span className="text-[10.5px] text-surface-400">{size === "sm" ? "Lists" : size === "md" ? "Sidebar" : "Cards"}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={clearPicked}>Cancel</Button>
                <AsyncButton onClick={() => save()}>Save photo</AsyncButton>
              </div>
            </>
          ) : (
            <>
              <dl className="flex flex-col divide-y divide-surface-100">
                {details.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="profile-card-row flex items-center gap-3 py-2.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-100 text-surface-500">
                      <Icon size={15} aria-hidden="true" />
                    </span>
                    <dt className="w-24 shrink-0 text-[12px] font-semibold uppercase tracking-wide text-surface-400">{label}</dt>
                    <dd className="min-w-0 flex-1 truncate text-right text-[13.5px] font-medium text-surface-800">{value}</dd>
                  </div>
                ))}
              </dl>
              <div className="flex flex-wrap justify-center gap-2">
                <FileInput
                  variant="button"
                  size="sm"
                  accept="image/jpeg,image/png,image/webp"
                  value={null}
                  placeholder={me.photoUrl ? "Change photo" : "Upload photo"}
                  onChange={f => void pickFile(f)}
                />
                {me.photoUrl && (
                  <AsyncButton
                    variant="outline"
                    size="sm"
                    animated={false}
                    onClick={() => remove()}
                    confirm={{
                      title: "Remove your photo?",
                      description: "Your initials will show instead.",
                      actionText: "Remove",
                      tone: "destructive",
                    }}
                  >
                    Remove photo
                  </AsyncButton>
                )}
              </div>
            </>
          )}
          {err && <p role="alert" className="text-center text-[12.5px] font-medium text-error">{err}</p>}
          <p className="text-center text-[11.5px] leading-snug text-surface-400">{PHOTO_RULES_TEXT}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
